import { PDFReaderBrowser } from '@/components/file-viewers/reactPdf';
import { DocumentTabs } from '@/components/contacts/candidate-job-drawer/tabs/current-job/details/OfferV1/components/offer-template/DocumentTabs';
import { useShowToast } from '@/hooks';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import {
  useApproveRejectActionMutation,
  useLazyGetApprovalsByEntityQuery,
} from '@/store/query/approval-builder';
import { useLazyGetOfferByIdQuery } from '@/store/query/offer-Initiation';
import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  Spinner,
  useDisclosure,
  Center,
  useBreakpointValue,
} from '@chakra-ui/react';
import { XIcon } from '@heroicons/react/solid';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { MinusCircle } from '@kula-ai/icons/outline/MinusCircle';
import { useState, useEffect } from 'react';
import { ApprovalFlowSteps } from '../ApprovalFlowSteps';
import { Drawer } from '../drawer';
import { OfferDetails } from './OfferDetails';
import { ModalWithForm } from '@/components/ats';
import { KText, KModal } from '@/ui-library/components';
import { useIsMobile } from '@/hooks/use-IsMobile';
import { MOBILE_BREAKPOINT } from '@/constants/mobile-breakpoints';
import { useAuth } from '@/hooks/use-auth';
import { useKFlags } from '@/hooks/use-k-flags';
import { useHistory } from 'react-router-dom';

export const OfferApprovalView = ({
  modalActions,
  offerId,
  onSuccess = () => {},
  hideApprovalStatus = false,
}) => {
  const isMobile = useIsMobile({ mobileBreakpoint: MOBILE_BREAKPOINT });
  const { isBootstraping } = useAuth();
  const history = useHistory();

  const rejectApproveFields = [
    {
      name: 'note',
      type: 'textArea',
      label: 'Notes',
      maxLength: 250,
      placeholder: 'Write your reason here',
    },
  ];

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const approverActions = useDisclosure();
  const modalSize = useBreakpointValue({ base: 'full', md: 'md' });

  const [getCurrentOffer, { data: currentOffer, isFetching: isOfferFetching }] =
    useLazyGetOfferByIdQuery({});

  const [
    getApprovalData,
    { data: currentApproval, isFetching: isCurrentApprovalLoading },
  ] = useLazyGetApprovalsByEntityQuery();

  const { currUserId } = useAtsPermissions();
  const statusMenuActions = useDisclosure();
  const { tOfferTemplateMultiDocuments } = useKFlags();

  const {
    offer_document_pdf,
    offer_documents_pdf,
    offer_status,
    ats_offer_template,
  } = currentOffer || {};
  const isMultiDoc =
    !!tOfferTemplateMultiDocuments &&
    (ats_offer_template?.offer_documents?.length || 0) > 0;
  const [selectedDocIndex, setSelectedDocIndex] = useState(0);

  const isPendingApproval = offer_status === 'pending_approval';
  useEffect(() => {
    if (offerId) {
      getCurrentOffer({ id: offerId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  useEffect(() => {
    if (isPendingApproval) {
      getApprovalData({ id: offerId, type: 'offer' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, currentOffer]);

  useEffect(() => {
    setSelectedDocIndex(0);
  }, [currentOffer?.id]);

  const currentActiveStep = currentApproval?.approval_steps.find(
    (step) => step?.status === 'in_progress'
  );
  const currentStepUsers =
    currentActiveStep?.approval_user_groups?.[0]?.approval_users
      .filter((user) => user?.approval_log === null)
      ?.map((userObj) => userObj?.user?.id);
  // Only check if user is approver when currUserId is available (not bootstrapping)
  const isCurrentUserApprover =
    !isBootstraping && currUserId && currentStepUsers?.includes(currUserId);

  const toast = useShowToast();

  const [approverAcceptAction, { isLoading: isApproveActionLoading }] =
    useApproveRejectActionMutation();

  const [approverRejectAction, { isLoading: isRejectActionLoading }] =
    useApproveRejectActionMutation();

  const handleDrawerSubmit = () => {
    approverAcceptAction({
      payload: {
        type: 'offer',
        entity_id: offerId,
        log_action: 'approve',
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to approve the offer`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `Offer approved successfully`,
        });
        if (isMobile) {
          history.push('/tasks/jobs/approvals');
          modalActions?.onClose();
          return;
        }
        modalActions?.onClose();
        onSuccess();
      }
    });
  };

  const handleApproveOnBehalf = (userId) => {
    approverAcceptAction({
      payload: {
        type: 'offer',
        entity_id: offerId,
        log_action: 'approve',
        user_id: userId,
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to approve the offer`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `Offer approved successfully`,
        });
        approverActions?.onClose();
        statusMenuActions?.onClose();
        onSuccess();
        if (isMobile) {
          modalActions?.onClose();
        }
      }
    });
  };

  const handleReject = (values) => {
    approverRejectAction({
      payload: {
        type: 'offer',
        entity_id: offerId,
        log_action: 'reject',
        note: values?.note || '--',
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to reject the offer`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `Offer has been rejected`,
        });
        modalActions?.onClose();
        setShowRejectModal(false);
        onSuccess();
      }
    });
  };

  const renderPdf = () => {
    const activePdf = isMultiDoc
      ? offer_documents_pdf?.[selectedDocIndex]
      : offer_document_pdf;
    const { url, content_type } = activePdf || {};

    const docTabs = ats_offer_template?.offer_documents?.length
      ? ats_offer_template.offer_documents.map((d) => ({
          filename: d.filename,
        }))
      : (offer_documents_pdf || []).map((pdf) => ({ filename: pdf.filename }));

    return (
      <Flex
        gap="0"
        h={{ base: '70vh', md: '80vh' }}
        mt={{ base: 2, md: 4 }}
        borderTop={isMultiDoc ? '1px solid' : undefined}
        borderRight={isMultiDoc ? '1px solid' : undefined}
        borderColor="border-color"
        pt={isMultiDoc ? '10px' : undefined}
      >
        {isMultiDoc && (
          <DocumentTabs
            docs={docTabs}
            selectedIndex={selectedDocIndex}
            onSelect={setSelectedDocIndex}
          />
        )}
        <Box flex="1" h="100%" borderRadius="md" overflow="hidden">
          <PDFReaderBrowser
            file={url}
            content_type={content_type}
            preview_url={null}
          />
        </Box>
      </Flex>
    );
  };

  const moreCtaConfig = [
    {
      id: 'reject',
      Comp: () => {
        return (
          <Button
            variant="secondary"
            w={{ base: '100%', md: 'auto' }}
            onClick={() => {
              setShowRejectModal(true);
            }}
            loadingText="Updating"
            isLoading={isRejectActionLoading}
            disabled={isApproveActionLoading}
          >
            <Icon mr="5px" color="ats-negative" as={MinusCircle} />
            <KText size="sm-medium" color="primary-500">
              Reject
            </KText>
          </Button>
        );
      },
    },
  ];

  const renderStatus = (showBorder = true) => {
    return (
      <Flex
        gap="0.5em"
        alignItems="center"
        border={showBorder && '1px solid'}
        borderRadius="6px"
        w="fit-content"
        p="0.125em 0.5em"
        borderColor="ats-border-color"
      >
        <KText color="secondary-900" size="sm-semi-bold">
          Approval status
        </KText>
      </Flex>
    );
  };

  const RenderStatusAction = () => {
    const { isOpen, onOpen, onClose } = statusMenuActions;

    if (currentOffer?.offer_status === 'pending_approval')
      return (
        <Box mt={{ base: 2, md: '24px' }} mr={{ base: 0, md: '24px' }}>
          <Menu isOpen={isOpen} onClose={onClose}>
            <MenuButton
              as={Button}
              onClick={onOpen}
              w="fit-content"
              variant="ats-secondary"
              rightIcon={<ChevronDown />}
            >
              {renderStatus(false)}
            </MenuButton>
            <MenuList
              w={{ base: '100%', md: '400px' }}
              maxH={{ base: '60vh', md: 'unset' }}
              overflowY="auto"
              boxShadow="drop-shadows-bottom"
              zIndex={99}
            >
              <ApprovalFlowSteps
                config={currentApproval}
                onClose={onClose}
                isLoading={isCurrentApprovalLoading}
                offerId={offerId}
                entityId={offerId}
                entityType="offer"
                onSuccess={() => {
                  getApprovalData({ id: offerId, type: 'offer' });
                }}
                renderModalInParent={true}
                handleModalOpen={approverActions.onOpen}
                handleUserSelect={setSelectedUser}
              />
            </MenuList>
          </Menu>
        </Box>
      );
    else {
      return (
        <>
          {statusMap[currentOffer?.offer_status] && (
            <MenuButton
              as={Button}
              onClick={(event) => {
                event?.stopPropagation();
                onOpen();
              }}
              variant="ats-pending"
              borderColor={'transparent'}
              rightIcon={
                <ChevronDown color="#D98647" height="16px" width={'16px'} />
              }
              maxH="24px"
            >
              <KText color="#D98647" size="sm-semi-bold">
                {statusMap[currentOffer?.offer_status]}
              </KText>
            </MenuButton>
          )}
        </>
      );
    }
  };

  const statusMap = {
    draft: 'draft',
    approved_internal: 'offer approved',
    rejected_internal: 'offer rejected',
    candidate_email_in_progress: 'sending offer',
    candidate_email_failed: 'unable to send offer',
    awaiting_on_candidate: 'offer sent',
    accepted_by_candidate: 'accepted by candidate',
    rejected_by_candidate: 'rejected by candidate',
  };

  return (
    <>
      {modalActions?.isOpen && (
        <Drawer
          drawerActions={modalActions}
          hideCloseButton={isMobile}
          onDrawerSubmit={handleDrawerSubmit}
          drawerContentProps={{
            minW: { base: '100%', md: '750px' },
            p: { base: 0, md: 2 },
            borderTopLeftRadius: { base: 0, md: 'xl' },
          }}
          drawerBodyProps={{
            overflow: isMobile ? 'auto' : 'hidden',
          }}
          headerRightAdditionalComponent={
            !hideApprovalStatus && <RenderStatusAction />
          }
          disableCancel
          moreCtaConfig={moreCtaConfig}
          showHeader={!isMobile}
          headerText="Approve Offer"
          headerDescription={
            !isMobile
              ? 'This is a preview of the offer document that will be sent'
              : undefined
          }
          showFooter={!isMobile && isCurrentUserApprover && isPendingApproval}
          ctaProps={{
            loadingText: 'Updating',
            isLoading: isApproveActionLoading,
            disabled: isRejectActionLoading,
            w: { base: '100%', md: 'auto' },
          }}
          ctaLabel="Approve offer"
        >
          {isMobile && (
            <Box position="relative">
              <Center
                w="48px"
                h="48px"
                left="12px"
                top="3px"
                pos="absolute"
                bg="#fff"
                borderRadius="full"
                cursor="pointer"
                onClick={modalActions?.onClose}
                zIndex={1}
              >
                <Icon as={XIcon} w="20px" h="20px" />
              </Center>

              <Box pl="64px" pr="16px" py="12px">
                <KText size="lg-semi-bold" color="secondary-900">
                  Offer approval
                </KText>
              </Box>
            </Box>
          )}

          <Flex
            h="100%"
            flexDir="column"
            gap={{ base: '6px', md: '10px' }}
            p={{ base: '8px', md: '8px 16px' }}
            pb={
              isMobile && isCurrentUserApprover && isPendingApproval
                ? '100px'
                : undefined
            }
            overflowY="auto"
          >
            {isOfferFetching || (isPendingApproval && isBootstraping) ? (
              <Flex
                h="100%"
                w="100%"
                alignItems="center"
                justifyContent="center"
              >
                <Spinner color="primary-800" />
              </Flex>
            ) : (
              <>
                <OfferDetails offer={currentOffer} isPreviewMobile={isMobile} />
                {renderPdf()}
              </>
            )}
          </Flex>

          {/* --- MOBILE FOOTER BUTTONS --- */}
          {isMobile && isCurrentUserApprover && isPendingApproval && (
            <Flex
              position="fixed"
              bottom="0"
              left="0"
              w="100%"
              p="12px"
              bg="white"
              boxShadow="drop-shadows-bottom"
              zIndex="999"
              gap="8px"
            >
              {/* Reject Button */}
              <Button
                flex="1"
                variant="mobile-danger-outline"
                onClick={() => setShowRejectModal(true)}
                isLoading={isRejectActionLoading}
                disabled={isApproveActionLoading}
              >
                Reject
              </Button>

              <Button
                flex="1"
                variant="mobile-primary-lg"
                onClick={handleDrawerSubmit}
                isLoading={isApproveActionLoading}
                disabled={isRejectActionLoading}
              >
                Approve Offer
              </Button>
            </Flex>
          )}
        </Drawer>
      )}
      {showRejectModal && (
        <ModalWithForm
          isOpen={true}
          onClose={() => {
            setShowRejectModal(false);
          }}
          headerText={'Reason for rejection?'}
          subHeaderText={
            'Provide detailed feedback on why you are rejecting this offer'
          }
          primaryCtaText="Reject"
          formConfig={rejectApproveFields}
          handleSubmit={handleReject}
          isCtaLoading={isRejectActionLoading}
          formContainerStyles={{
            m: { base: '0', md: '-10px 0' },
          }}
          skipDirtyCheck={true}
        />
      )}
      {approverActions?.isOpen && (
        <KModal
          isOpen={approverActions?.isOpen}
          onClose={approverActions?.onClose}
          title={`Approve on behalf of ${selectedUser?.user?.name || ''}`}
          ctaLabel="Approve"
          loadingText="Approving"
          ctaAction={(event) => {
            event?.stopPropagation();
            if (selectedUser?.user?.id) {
              handleApproveOnBehalf(selectedUser.user.id);
            }
          }}
          size={modalSize}
          closeOnOverlayClick={false}
        >
          <Flex flexDir="column" gap="10px">
            <KText>
              Approving for {selectedUser?.user?.name} will move the approval
              flow forward as though they did it.
            </KText>
            <KText mb="10px">This is permanent and cannot be reversed.</KText>
          </Flex>
        </KModal>
      )}
    </>
  );
};
